import { useTranslation } from 'react-i18next';
import Logo from '../../assets/logo.svg';
import LogoMerchant from '../../assets/logo-merchant.svg';
import { Avatar, AvatarFallback } from '@ui/avatar';
import { useGlassStyles } from '../../hooks/useGlassStyles';
import { getContrastColor } from '../../styles/glass-effects';
import { useState, useEffect, useRef } from 'react';
import { getBackgroundColor } from '@hooks/getBackgroundColor';
import { useAuth } from '@hooks/useAuth';
import { useMembership } from '../../hooks/useMembership';
import { useTierIcon } from '@hooks/useTierIcon';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguageChange } from '@hooks/languageChange';
import { useAuthDialog } from '../../contexts/AuthDialogContext';
import { useCallDialog } from '../../contexts/CallDialogContext';
import { useToast } from '../../contexts/ToastContext';
import { ImageWithFallback } from '../../utils/imageFallback.tsx';
import { UserIcon, PhoneIcon } from '@heroicons/react/20/solid';
import getAvatarColor from '@hooks/getAvatarColor';
import getUserInitials from '@hooks/getUserInitials';
import bronzeIcon from '../../assets/loyalty/bronze.png';
import silverIcon from '../../assets/loyalty/silver.png';
import goldIcon from '../../assets/loyalty/gold.png';
// import fireAnimation from '../../assets/animate/20251003-215725.gif';
import ThadingyutAnimation from '../../components/ThadingyutAnimation/ThadingyutAnimation';
import { Store01FreeIcons } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import CashInDialog from '../CashInDialog/CashInDialog';
import walletIcon from '../../assets/wallet/ez-wallet.png';

interface HeaderProps {
  currentSlide?: number;
  headerColors?: string[];
}

function Header({ currentSlide = 0, headerColors = ['rgb(0, 65, 122)'] }: HeaderProps) {
  const { i18n, t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const [showThadingyutAnimation, setShowThadingyutAnimation] = useState(false);
  const [isRoleSwitching, setIsRoleSwitching] = useState(false);
  const [showCashInDialog, setShowCashInDialog] = useState(false);

  const { changeLanguage } = useLanguageChange();

  // Custom language change handler that updates URL
  const handleLanguageChange = async (newLanguage: string) => {
    await changeLanguage(newLanguage);

    // Update URL with language parameter
    const currentUrl = new URL(window.location.href);
    if (newLanguage === 'mm') {
      currentUrl.searchParams.set('lang', 'my');
    } else {
      currentUrl.searchParams.delete('lang');
    }

    // Update the URL without page reload
    window.history.replaceState({}, '', currentUrl.toString());
  };

  const { showAuthDialog } = useAuthDialog();
  const { openCallDialog } = useCallDialog();
  const { showToast } = useToast();

  const [bgColor, setBgColor] = useState('rgb(255, 255, 255)'); // Default background color
  const elementRef = useRef<HTMLDivElement>(null);
  const { user, isAuthenticated, logout, switchRole } = useAuth();
  const { membershipInfo } = useMembership();
  const tierIconProps = useTierIcon();
  const navigate = useNavigate();
  const location = useLocation();

  // Function to check if a menu item is active
  const isActiveRoute = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const currentColor =
    headerColors && headerColors.length > 0
      ? headerColors[currentSlide] || headerColors[0]
      : 'rgb(0, 65, 122)';

  const { glassStyle, waterDropStyle } = useGlassStyles(currentColor);
  const textColorClass = getContrastColor(currentColor);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    if (elementRef.current) {
      getBackgroundColor(elementRef.current).then((color) => setBgColor(color));
    }
  }, [currentSlide]);

  // Add useEffect for handling outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMenuOpen &&
        (!(event.target instanceof Element) || !event.target.closest('.menu-container'))
      ) {
        setIsMenuOpen(false);
      }
      if (
        isDesktopMenuOpen &&
        (!(event.target instanceof Element) || !event.target.closest('.desktop-menu-container'))
      ) {
        setIsDesktopMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen, isDesktopMenuOpen]);

  return (
    <>
      <header
        ref={elementRef}
        className="fixed w-full top-0 z-50 backdrop-blur-[20px] bg-white/5 backdrop-saturate-[180%] 
        shadow-[0_4px_30px_rgba(0,0,0,0.1)] 
        transition-all duration-500 px-4 safe-top"
        style={glassStyle}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <ImageWithFallback
                src={user?.activeRole === 'merchant' ? LogoMerchant : Logo}
                alt={t('alt_texts.logo')}
                className="object-cover h-[40px] w-[40px]"
              />
              {/* <div
                onClick={(e) => {
                  e.stopPropagation();
                  setShowThadingyutAnimation(true);
                }}
                className="cursor-pointer hover:scale-110 transition-transform duration-300 h-[40px]"
              >
                <ImageWithFallback
                  src={fireAnimation}
                  alt={t('thadingyut.logo')}
                  className="object-cover h-[40px]"
                />
              </div> */}
              {/* {user?.role === 'merchant' && (
                <span className="ml-2 text-xs text-bold text-cyan-800 bg-cyan-100/75 px-2 py-1 rounded-md font-medium">
                  Merchant
                </span>
              )} */}
            </div>
            <div className="h-[60px] w-auto sm:w-full flex justify-end">
              <div className="flex items-center sm:px-6 sm:gap-8 md:px-6 md:gap-8">
                {/* Language Button */}
                <button
                  type="button"
                  className="hidden md:block w-[60px] rounded-full bg-white/40 
                backdrop-blur-xl px-2.5 py-1 text-sm font-semibold 
                shadow-sm ring-1 ring-white/30 
                hover:bg-white/50 hover:cursor-pointer 
                transition-all duration-300
                hover:shadow-lg active:scale-95"
                  style={glassStyle}
                  onClick={() => handleLanguageChange(i18n.language === 'en' ? 'mm' : 'en')}
                >
                  <span
                    className={`font-size-[11px] font-semibold text-gray-900 ${currentColor === 'rgb(0, 65, 122)' ? 'text-' + bgColor : 'text-' + textColorClass}`}
                  >
                    {i18n.language === 'en' ? 'မြန်မာ' : 'ENG'}
                  </span>
                </button>

                {/* Navigation Links */}
                <button
                  type="button"
                  className={`hidden md:block font-size-[12px] font-semibold ${currentColor === 'rgb(0, 65, 122)' ? 'text-' + bgColor : 'text-' + textColorClass} 
                ${isActiveRoute('/') ? 'opacity-100 border-b-2 border-current' : 'opacity-80 hover:opacity-100'} 
                transition-all duration-300`}
                  onClick={() => navigate('/')}
                >
                  Home
                </button>
                <button
                  type="button"
                  className={`hidden md:block font-size-[12px] font-semibold ${currentColor === 'rgb(0, 65, 122)' ? 'text-' + bgColor : 'text-' + textColorClass} 
                ${isActiveRoute('/about-us') ? 'opacity-100 border-b-2 border-current' : 'opacity-80 hover:opacity-100'} 
                transition-all duration-300`}
                  onClick={() => navigate('/about-us')}
                >
                  About Us
                </button>
                <button
                  type="button"
                  className={`hidden md:block font-size-[12px] font-semibold ${currentColor === 'rgb(0, 65, 122)' ? 'text-' + bgColor : 'text-' + textColorClass} 
                ${isActiveRoute('/support') ? 'opacity-100 border-b-2 border-current' : 'opacity-80 hover:opacity-100'} 
                transition-all duration-300`}
                  onClick={() => navigate('/support')}
                >
                  Support
                </button>

                {/* Call Center Phone Number */}
                <button
                  type="button"
                  onClick={openCallDialog}
                  className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 
                         bg-white/10 hover:bg-white/20 backdrop-blur-sm
                         ${currentColor === 'rgb(0, 65, 122)' ? 'text-' + bgColor : 'text-' + textColorClass}
                         rounded-full border border-white/20
                         transition-all duration-300 hover:shadow-md
                         font-medium text-xs opacity-90 hover:opacity-100`}
                  title="Call Center - Monday to Friday 9:00 AM to 5:00 PM Support"
                >
                  <PhoneIcon className="h-3.5 w-3.5" />
                  <span>Call Center</span>
                </button>
              </div>

              {/* User Profile Button */}
              <div className="flex items-center gap-2 group relative menu-container desktop-menu-container">
                <div className="flex items-center gap-2">
                  <div
                    role="button"
                    onClick={() => setIsDesktopMenuOpen(!isDesktopMenuOpen)}
                    className={`${isMenuOpen && 'hidden'} flex items-center gap-2 rounded-full 
                  pl-1.5 pr-3 py-1.5 transition-all duration-300
                  hover:shadow-lg active:scale-95 cursor-pointer`}
                    style={{ ...glassStyle, ...waterDropStyle } as React.CSSProperties}
                  >
                    <Avatar
                      className={`h-8 w-8 ring-2 ring-white/30 ${getAvatarColor(user?.name || 'Guest')} text-white flex items-center justify-center font-semibold`}
                      loyaltyTier={
                        membershipInfo
                          ? (membershipInfo?.currentTier?.name?.toLowerCase() as
                              | 'bronze'
                              | 'silver'
                              | 'gold') || 'bronze'
                          : undefined
                      }
                      showLoyaltyIcon={membershipInfo ? true : false}
                      enableCoinAnimation={membershipInfo ? true : false}
                      showProgressCircle={false}
                      currentPoints={membershipInfo?.pointsBalance || 0}
                      nextLevelPoints={membershipInfo?.currentTier?.maxPoints || 1000}
                      loyaltyDisplayMode={membershipInfo ? 'flip' : undefined}
                      isMerchant={user?.activeRole === 'merchant'}
                    >
                      {/* <AvatarImage src="https://github.com/shadcn.png" />
                    <AvatarFallback>CN</AvatarFallback> */}
                      {/* {getUserInitials(user?.name || '')} */}
                      {user?.activeRole === 'merchant' ? (
                        <HugeiconsIcon
                          icon={Store01FreeIcons}
                          size={16}
                          color="white"
                          strokeWidth={2}
                          className="mb-2"
                        />
                      ) : (
                        getUserInitials(user?.name || '')
                      )}
                      {!user?.id && (
                        <AvatarFallback>
                          <UserIcon className="h-4 w-4 text-black" />
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <span
                      className={`hidden md:block text-sm font-medium ${currentColor === 'rgb(0, 65, 122)' ? 'text-' + bgColor : 'text-' + textColorClass} opacity-90 group-hover:opacity-100 transition-all duration-300 z-10`}
                    >
                      {isAuthenticated && user ? user.name || user.email : 'Guest'}
                    </span>
                    {/* Desktop dropdown arrow */}
                    <svg
                      className="hidden md:block w-4 h-4 transition-transform duration-200 z-10"
                      style={{
                        transform: isDesktopMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        color: currentColor === 'rgb(0, 65, 122)' ? bgColor : textColorClass,
                      }}
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M6 9l6 6 6-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <button
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLanguageChange(i18n.language === 'en' ? 'mm' : 'en');
                      }}
                      className="md:hidden md:block w-[60px] rounded-full bg-white/40 backdrop-blur-xl px-2.5 py-1 text-sm font-semibold shadow-sm ring-1 ring-white/30 hover:bg-white/50 hover:cursor-pointer transition-all duration-300 hover:shadow-lg active:scale-95"
                      style={glassStyle}
                    >
                      <span
                        className={`font-size-[12px] font-semibold text-gray-900 ${currentColor === 'rgb(0, 65, 122)' ? 'text-' + bgColor : 'text-' + textColorClass}`}
                      >
                        {i18n.language === 'en' ? 'မြန်မာ' : 'ENG'}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Desktop Dropdown Menu */}
                {isDesktopMenuOpen && (
                  <div
                    className="hidden md:block absolute top-full right-0 mt-2 w-48 rounded-lg shadow-lg overflow-hidden z-50"
                    style={{ ...glassStyle, ...waterDropStyle } as React.CSSProperties}
                  >
                    <div
                      className={`${textColorClass === 'white' ? 'bg-white/90' : 'bg-[#1C1C1E]/90'} backdrop-blur-[14px] border ${textColorClass === 'white' ? 'border-gray-200' : 'border-white/20'}`}
                    >
                      {isAuthenticated ? (
                        <>
                          {/* User Points and Tier Section */}
                          {membershipInfo && (
                            <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r rounded-t-lg from-gray-50 to-gray-100">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center justify-center w-8 h-8 rounded-full">
                                    <img
                                      src={
                                        tierIconProps.loyaltyTier === 'bronze'
                                          ? bronzeIcon
                                          : tierIconProps.loyaltyTier === 'silver'
                                            ? silverIcon
                                            : tierIconProps.loyaltyTier === 'gold'
                                              ? goldIcon
                                              : bronzeIcon
                                      }
                                      alt="Tier Icon"
                                      className="w-8 h-8 object-contain"
                                    />
                                  </div>
                                  <div>
                                    <p className="text-xs font-medium text-gray-600 uppercase">
                                      {membershipInfo?.currentTier?.name?.toLowerCase() ||
                                        t('loyalty.not_available')}
                                    </p>
                                    <p className="text-sm font-bold text-gray-900">
                                      {membershipInfo?.pointsBalance?.toLocaleString() || '0'}{' '}
                                      Points
                                    </p>
                                  </div>
                                </div>
                              </div>
                              {/* Progress Bar */}
                              <div className="mt-2">
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                  <div
                                    className="h-1.5 rounded-full transition-all duration-300"
                                    style={{
                                      background: getTierGradient(
                                        membershipInfo?.currentTier?.name,
                                      ),
                                      width: `${getTierProgressWidth(
                                        membershipInfo?.pointsBalance,
                                        membershipInfo?.currentTier,
                                      )}%`,
                                    }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Wallet Section */}
                          <div
                            onClick={() => {
                              setShowCashInDialog(true);
                              setIsDesktopMenuOpen(false);
                            }}
                            className={`px-4 py-3 border-b cursor-pointer ${textColorClass === 'white' ? 'border-gray-100 bg-white/5' : 'border-white/10 bg-black/20'}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-8 h-8 flex items-center justify-center ${textColorClass === 'white' ? 'bg-white/20 text-white' : 'bg-black/20 text-gray-200'}`}
                                >
                                  <img
                                    src={walletIcon}
                                    alt="Wallet Icon"
                                    className="w-8 h-8 object-contain"
                                  />
                                </div>
                                <div className="flex flex-col">
                                  <span
                                    className={`text-[10px] font-semibold uppercase tracking-wider leading-tight ${textColorClass === 'white' ? 'text-gray-500' : 'text-gray-400'}`}
                                  >
                                    Wallet Balance
                                  </span>
                                  <span
                                    className={`text-sm font-bold leading-tight font-mono ${textColorClass === 'white' ? 'text-gray-900' : 'text-white'}`}
                                  >
                                    {(
                                      user?.balances?.MMK?.available ??
                                      user?.walletBalance ??
                                      0
                                    ).toLocaleString()}{' '}
                                    <span
                                      className={`text-[10px] font-sans font-normal ${textColorClass === 'white' ? 'text-gray-500' : 'text-gray-400'}`}
                                    >
                                      MMK
                                    </span>
                                  </span>
                                </div>
                              </div>
                              {/* <button
                                onClick={() => {
                                  setShowCashInDialog(true);
                                  setIsDesktopMenuOpen(false);
                                }}
                                className="px-3 py-1.5 bg-[#04BFBF] text-white text-[11px] font-bold rounded-full hover:bg-[#038C8C] transition-colors shadow-sm"
                              >
                                Top Up
                              </button> */}
                            </div>
                          </div>

                          {/* Account Switch Section - Show if user has activeRole and (has merchantId OR activeRole is merchant) */}
                          {user?.activeRole &&
                            (user?.merchantId || user?.activeRole === 'merchant') && (
                              <div
                                className={`border-b ${textColorClass === 'white' ? 'border-gray-100' : 'border-white/10'}`}
                              >
                                <div className="px-2 py-2">
                                  <button
                                    onClick={async () => {
                                      if (isRoleSwitching) return; // Prevent multiple clicks

                                      setIsRoleSwitching(true);
                                      try {
                                        const newRole =
                                          user?.activeRole === 'merchant' ? 'user' : 'merchant';
                                        await switchRole(newRole);
                                        // Toast will be shown after page reload
                                        setIsDesktopMenuOpen(false);
                                      } catch {
                                        // Only show error toast immediately since role switch failed
                                        showToast('error', 'Failed to switch account');
                                      } finally {
                                        setIsRoleSwitching(false);
                                      }
                                    }}
                                    disabled={isRoleSwitching}
                                    className={`w-full px-3 py-2 text-xs rounded-md transition-colors duration-200 flex items-center justify-center gap-2 ${
                                      isRoleSwitching
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                        : textColorClass === 'white'
                                          ? 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                                          : 'bg-white/20 text-white border border-white/30 hover:bg-white/30'
                                    }`}
                                  >
                                    {isRoleSwitching ? (
                                      <>
                                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current"></div>
                                        Switching...
                                      </>
                                    ) : user?.activeRole === 'merchant' ? (
                                      <>
                                        <UserIcon className="h-3.5 w-3.5" />
                                        Switch to User
                                      </>
                                    ) : (
                                      <>
                                        <HugeiconsIcon
                                          icon={Store01FreeIcons}
                                          size={14}
                                          strokeWidth={2}
                                        />
                                        Switch to Merchant
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            )}

                          <button
                            onClick={() => {
                              navigate('/account-settings');
                              setIsDesktopMenuOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-gray-700 hover:bg-gray-50' : 'text-white hover:bg-white/10'} transition-colors duration-200 text-left border-b ${textColorClass === 'white' ? 'border-gray-100' : 'border-white/10'}`}
                          >
                            Account Settings
                          </button>
                          <button
                            onClick={() => {
                              navigate('/orders');
                              setIsDesktopMenuOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-gray-700 hover:bg-gray-50' : 'text-white hover:bg-white/10'} transition-colors duration-200 text-left border-b ${textColorClass === 'white' ? 'border-gray-100' : 'border-white/10'}`}
                          >
                            Order History
                          </button>

                          <button
                            onClick={() => {
                              navigate('/rates');
                              setIsDesktopMenuOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-gray-700 hover:bg-gray-50' : 'text-white hover:bg-white/10'} transition-colors duration-200 text-left border-b ${textColorClass === 'white' ? 'border-gray-100' : 'border-white/10'}`}
                          >
                            Rates
                          </button>

                          <button
                            onClick={() => {
                              logout();
                              showToast('success', t('messages.logged_out_successfully'));
                              setIsDesktopMenuOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-sm text-red-600 ${textColorClass === 'white' ? 'hover:bg-red-50' : 'hover:bg-red-900/20'} transition-colors duration-200 text-left`}
                          >
                            Logout
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              navigate('/account-settings');
                              setIsDesktopMenuOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-gray-700 hover:bg-gray-50' : 'text-white hover:bg-white/10'} transition-colors duration-200 text-left border-b ${textColorClass === 'white' ? 'border-gray-100' : 'border-white/10'}`}
                          >
                            Account Settings
                          </button>
                          <button
                            onClick={() => {
                              showAuthDialog('login');
                              setIsDesktopMenuOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-gray-700 hover:bg-gray-50' : 'text-white hover:bg-white/10'} transition-colors duration-200 text-left border-b ${textColorClass === 'white' ? 'border-gray-100' : 'border-white/10'}`}
                          >
                            Login
                          </button>
                          <button
                            onClick={() => {
                              showAuthDialog('signup');
                              setIsDesktopMenuOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-gray-700 hover:bg-gray-50' : 'text-white hover:bg-white/10'} transition-colors duration-200 text-left border-b ${textColorClass === 'white' ? 'border-gray-100' : 'border-white/10'}`}
                          >
                            Sign Up
                          </button>
                          <button
                            onClick={() => {
                              navigate('/orders');
                              setIsDesktopMenuOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-gray-700 hover:bg-gray-50' : 'text-white hover:bg-white/10'} transition-colors duration-200 text-left ${textColorClass === 'white' ? 'border-gray-100' : 'border-white/10'}`}
                          >
                            Order History
                          </button>
                          <button
                            onClick={() => {
                              navigate('/rates');
                              setIsDesktopMenuOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-gray-700 hover:bg-gray-50' : 'text-white hover:bg-white/10'} transition-colors duration-200 text-left border-b ${textColorClass === 'white' ? 'border-gray-100' : 'border-white/10'}`}
                          >
                            Rates
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {/* Mobile Navigation - Phone and Menu */}
                <div className={`${isMenuOpen && 'hidden'} md:hidden flex items-center gap-2`}>
                  {/* Mobile Phone Button */}
                  <button
                    type="button"
                    onClick={openCallDialog}
                    className="w-10 h-10 rounded-full flex items-center justify-center 
                           transition-all duration-300 hover:shadow-lg active:scale-95"
                    style={{ ...glassStyle, ...waterDropStyle } as React.CSSProperties}
                    aria-label="Call Center"
                  >
                    <PhoneIcon
                      className={`w-4 h-4 ${currentColor === 'rgb(0, 65, 122)' ? 'text-' + bgColor : 'text-' + textColorClass}`}
                    />
                  </button>

                  {/* Mobile Menu Button */}
                  <button
                    type="button"
                    className="w-10 h-10 rounded-full flex items-center justify-center 
                           transition-all duration-300 hover:shadow-lg active:scale-95"
                    style={{ ...glassStyle, ...waterDropStyle } as React.CSSProperties}
                    onClick={toggleMenu}
                    aria-label="Open menu"
                    aria-expanded={isMenuOpen}
                  >
                    <svg
                      className={`w-5 h-5 ${currentColor === 'rgb(0, 65, 122)' ? 'text-' + bgColor : 'text-' + textColorClass}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M19 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M5 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </button>
                </div>

                {isMenuOpen && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-40 transition-all duration-300 ease-in-out"
                      onClick={() => setIsMenuOpen(false)}
                    />

                    {/* Menu Modal */}
                    <div className="w-[60%] fixed top-0 right-0 p-2 z-50 transition-all duration-300 ease-out animate-in zoom-in-95">
                      <div
                        className="ml-auto max-w-md rounded-3xl shadow-lg overflow-hidden transition-all duration-300 ease-out animate-in zoom-in-95"
                        style={{ ...glassStyle, ...waterDropStyle } as React.CSSProperties}
                      >
                        {/* Menu Items */}
                        <div
                          className={`p-2 ${textColorClass === 'white' ? 'bg-white/80' : 'bg-[#1C1C1E]/70'} shadow-[0_4px_30px_rgba(0,0,0,0.1)] backdrop-blur-[14px]`}
                        >
                          <div className="flex flex-row gap-2 justify-between">
                            <div>
                              {/* Navigation Links - Support and About Us */}
                              <button
                                onClick={() => {
                                  navigate('/support');
                                  setIsMenuOpen(false);
                                }}
                                className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-black' : 'text-white'} 
                            rounded-lg hover:bg-white/10 transition-colors duration-200 
                            text-left mb-2`}
                              >
                                Support
                              </button>
                              <button
                                onClick={() => {
                                  navigate('/about-us');
                                  setIsMenuOpen(false);
                                }}
                                className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-black' : 'text-white'} 
                            rounded-lg hover:bg-white/10 transition-colors duration-200 
                            text-left mb-2`}
                              >
                                About Us
                              </button>

                              {isAuthenticated ? (
                                <>
                                  <button
                                    onClick={() => {
                                      navigate('/rates');
                                      setIsMenuOpen(false);
                                    }}
                                    className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-black' : 'text-white'} 
                                rounded-lg hover:bg-white/10 transition-colors duration-200 
                                text-left mb-2`}
                                  >
                                    Rates
                                  </button>

                                  <button
                                    onClick={() => {
                                      logout();
                                      showToast('success', t('messages.logged_out_successfully'));
                                      setIsMenuOpen(false);
                                    }}
                                    className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-black' : 'text-white'} 
                                rounded-lg hover:bg-white/10 transition-colors duration-200 
                                text-left mb-2`}
                                  >
                                    Logout
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => {
                                      showAuthDialog('login');
                                      setIsMenuOpen(false);
                                    }}
                                    className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-black' : 'text-white'} 
                                rounded-lg hover:bg-white/10 transition-colors duration-200 
                                text-left mb-2`}
                                  >
                                    Login
                                  </button>
                                  <button
                                    onClick={() => {
                                      showAuthDialog('signup');
                                      setIsMenuOpen(false);
                                    }}
                                    className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-black' : 'text-white'} 
                                rounded-lg hover:bg-white/10 transition-colors duration-200 
                                text-left mb-2`}
                                  >
                                    Sign Up
                                  </button>
                                  <button
                                    onClick={() => {
                                      navigate('/rates');
                                      setIsMenuOpen(false);
                                    }}
                                    className={`w-full px-4 py-3 text-sm ${textColorClass === 'white' ? 'text-black' : 'text-white'} 
                                  rounded-lg hover:bg-white/10 transition-colors duration-200 
                                  text-left`}
                                  >
                                    Rates
                                  </button>
                                </>
                              )}
                            </div>
                            <div className="flex w-fit justify-end">
                              <button
                                onClick={() => setIsMenuOpen(false)}
                                className="p-1 h-fit rounded-full hover:bg-white/10 transition-colors duration-200"
                              >
                                <svg
                                  className={`w-5 h-5 ${textColorClass === 'white' ? 'text-black' : 'text-white'}`}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M18 6L6 18M6 6l12 12"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>

                          {/* Mobile Wallet Section */}
                          {/* {isAuthenticated && (
                            <div className={`px-3 py-3 rounded-2xl mb-2 shadow-lg border
                                ${textColorClass === 'white' ? 'bg-white/10 border-white/20 text-white' : 'bg-gradient-to-r from-gray-900 to-gray-800 text-white border-transparent'}`}>
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex flex-col">
                                  <span className="text-[10px] uppercase font-medium tracking-wide opacity-70">Your Balance</span>
                                  <div className="flex items-baseline gap-1">
                                    <span className="text-xl font-bold font-mono">{(user?.walletBalance || 0).toLocaleString()}</span>
                                    <span className="text-xs opacity-70">MMK</span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    setIsMenuOpen(false);
                                    setShowCashInDialog(true);
                                  }}
                                  className="bg-white/10 p-2 rounded-full hover:bg-white/20 transition-colors"
                                >
                                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                  </svg>
                                </button>
                              </div>
                              <button
                                onClick={() => {
                                  setIsMenuOpen(false);
                                  setShowCashInDialog(true);
                                }}
                                className="w-full py-2 bg-white text-black text-xs font-bold rounded-xl hover:bg-gray-100 transition-colors"
                              >
                                Top Up Wallet
                              </button>
                            </div>
                          )} */}

                          {/* Mobile Wallet Section */}
                          {isAuthenticated && (
                            <div className="px-3 py-3 rounded-2xl bg-gradient-to-r from-[#04BFBF] to-[#038C8C] text-white mb-2 shadow-lg shadow-[#04BFBF]/20">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex flex-col">
                                  <span className="text-[10px] text-white/90 font-medium tracking-wide">
                                    YOUR BALANCE
                                  </span>
                                  <div className="flex items-baseline gap-1">
                                    <span className="text-xl font-bold font-mono">
                                      {(
                                        user?.balances?.MMK?.available ??
                                        user?.walletBalance ??
                                        0
                                      ).toLocaleString()}
                                    </span>
                                    <span className="text-xs text-white/80">MMK</span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    setIsMenuOpen(false);
                                    setShowCashInDialog(true);
                                  }}
                                  className="bg-white/10 p-2 rounded-full hover:bg-white/20 transition-colors"
                                >
                                  <svg
                                    className="w-5 h-5 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                    />
                                  </svg>
                                </button>
                              </div>
                              <button
                                onClick={() => {
                                  setIsMenuOpen(false);
                                  setShowCashInDialog(true);
                                }}
                                className="w-full py-2 bg-white text-black text-xs font-bold rounded-xl hover:bg-gray-100 transition-colors"
                              >
                                Top Up Wallet
                              </button>
                            </div>
                          )}

                          {/* User Points and Tier Section for Mobile */}
                          {isAuthenticated && membershipInfo && (
                            <div
                              className={`px-3 py-3 rounded-2xl bg-white border border-gray-200 ${user?.merchantId ? 'mb-2' : ''}`}
                            >
                              {' '}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center justify-center w-8 h-8 rounded-full">
                                    <img
                                      src={
                                        tierIconProps.loyaltyTier === 'bronze'
                                          ? bronzeIcon
                                          : tierIconProps.loyaltyTier === 'silver'
                                            ? silverIcon
                                            : tierIconProps.loyaltyTier === 'gold'
                                              ? goldIcon
                                              : bronzeIcon
                                      }
                                      alt="Tier Icon"
                                      className="w-8 h-8 object-contain"
                                    />
                                  </div>
                                  <div>
                                    <p className="text-xs font-medium text-gray-600 uppercase">
                                      {membershipInfo?.currentTier?.name?.toLowerCase() ||
                                        t('loyalty.not_available')}
                                    </p>
                                    <p className="text-sm font-bold text-gray-900">
                                      {membershipInfo?.pointsBalance?.toLocaleString() || '0'}{' '}
                                      Points
                                    </p>
                                  </div>
                                </div>
                              </div>
                              {/* Progress Bar */}
                              <div className="mt-2">
                                <div className="w-full bg-gray-200 rounded-full h-1">
                                  <div
                                    className="h-1 rounded-full transition-all duration-300"
                                    style={{
                                      background: getTierGradient(
                                        membershipInfo?.currentTier?.name,
                                      ),
                                      width: `${getTierProgressWidth(
                                        membershipInfo?.pointsBalance,
                                        membershipInfo?.currentTier,
                                      )}%`,
                                    }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Mobile Role Switch Button - Show if user has activeRole and (has merchantId OR activeRole is merchant) */}
                          {isAuthenticated &&
                            user?.activeRole &&
                            (user?.merchantId || user?.activeRole === 'merchant') && (
                              <button
                                onClick={async () => {
                                  if (isRoleSwitching) return; // Prevent multiple clicks

                                  setIsRoleSwitching(true);
                                  try {
                                    const newRole =
                                      user?.activeRole === 'merchant' ? 'user' : 'merchant';
                                    await switchRole(newRole);
                                    // Toast will be shown after page reload
                                    setIsMenuOpen(false);
                                  } catch {
                                    // Only show error toast immediately since role switch failed
                                    showToast('error', 'Failed to switch account');
                                  } finally {
                                    setIsRoleSwitching(false);
                                  }
                                }}
                                disabled={isRoleSwitching}
                                className={`w-full px-4 py-3 text-sm rounded-xl border transition-colors duration-200 text-left flex items-center gap-2 ${
                                  isRoleSwitching
                                    ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
                                    : textColorClass === 'white'
                                      ? 'text-black bg-blue-50/80 border-blue-200/50 hover:bg-opacity-80'
                                      : 'text-white bg-blue-900/50 border-blue-400/50 hover:bg-opacity-80'
                                }`}
                              >
                                {isRoleSwitching ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                    Switching...
                                  </>
                                ) : user?.activeRole === 'merchant' ? (
                                  <>
                                    <UserIcon className="h-4 w-4" />
                                    Switch to User
                                  </>
                                ) : (
                                  <>
                                    <HugeiconsIcon
                                      icon={Store01FreeIcons}
                                      size={16}
                                      strokeWidth={2}
                                    />
                                    Switch to Merchant
                                  </>
                                )}
                              </button>
                            )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      <ThadingyutAnimation
        isVisible={showThadingyutAnimation}
        onClose={() => setShowThadingyutAnimation(false)}
      />
      <CashInDialog
        isOpen={showCashInDialog}
        onClose={() => setShowCashInDialog(false)}
        currentBalance={user?.balances?.MMK?.available ?? user?.walletBalance ?? 0}
      />
    </>
  );
}

export default Header;
import { getTierGradient, getTierProgressWidth } from '../../utils/membershipProgress';
